import { tokenManager } from "../../api/tokenManager";

export interface FileDownloadOptions {
  entityName: string;
  fieldName: string;
  instanceId: string;
  fileName: string;
  localFileName?: string;
  // When true, open the fetched file in a new browser tab instead of forcing a
  // download. Used by the "Ver" affordance on PDF cells.
  inline?: boolean;
}

export async function downloadWithAuth(
  downloadUrlOrOptions: string | FileDownloadOptions,
  fileName?: string,
  activityStore?: any
): Promise<void> {
  const apiUrl =
    process.env.REACT_APP_API_URL || "http://localhost:4000/graphql";
  const serverUrl = apiUrl.replace(/\/graphql$/, "");

  let downloadUrl: string;
  let targetFileName: string;
  let inline = false;

  if (typeof downloadUrlOrOptions === "string") {
    downloadUrl = downloadUrlOrOptions;
    targetFileName = fileName || "download";
  } else {
    const {
      entityName,
      fieldName,
      instanceId,
      fileName: fn,
      localFileName,
    } = downloadUrlOrOptions;
    downloadUrl = `/${entityName}.${fieldName}/${instanceId}/${encodeURIComponent(
      fn
    )}`;
    targetFileName = localFileName || fn;
    inline = !!downloadUrlOrOptions.inline;
  }

  const activityId = activityStore?.startActivity("query", "fileDownload");

  try {
    const token = await tokenManager.getValidAccessToken();

    if (!token) {
      console.error("No se pudo obtener el token de autenticación");
      const link = document.createElement("a");
      link.href = `${serverUrl}/download${downloadUrl}`;
      link.download = targetFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (activityId) activityStore?.endActivity(activityId);
      return;
    }

    const response = await fetch(`${serverUrl}/download${downloadUrl}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (activityId) activityStore?.endActivity(activityId);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    if (inline) {
      // Open in a new browser tab; the blob URL stays alive long enough for
      // the tab to fetch it (the browser keeps a reference once it opens).
      window.open(blobUrl, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } else {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = targetFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    }

    if (activityId) activityStore?.endActivity(activityId);
  } catch (error) {
    if (activityId) activityStore?.endActivity(activityId);
    console.error("Error downloading file:", error);
    throw error;
  }
}

/**
 * Fetch a file as a Blob with the bearer token applied. Used by viewers that
 * need to consume the file contents in-process (e.g. ExcelViewer parses the
 * blob with SheetJS) instead of triggering a download / new-tab open.
 */
export async function fetchFileBlob(
  options: FileDownloadOptions,
  activityStore?: any
): Promise<Blob | null> {
  const apiUrl =
    process.env.REACT_APP_API_URL || "http://localhost:4000/graphql";
  const serverUrl = apiUrl.replace(/\/graphql$/, "");
  const { entityName, fieldName, instanceId, fileName } = options;
  const downloadUrl = `/${entityName}.${fieldName}/${instanceId}/${encodeURIComponent(
    fileName
  )}`;
  const activityId = activityStore?.startActivity("query", "fileFetch");
  try {
    const token = await tokenManager.getValidAccessToken();
    if (!token) {
      if (activityId) activityStore?.endActivity(activityId);
      return null;
    }
    const response = await fetch(`${serverUrl}/download${downloadUrl}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      if (activityId) activityStore?.endActivity(activityId);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    if (activityId) activityStore?.endActivity(activityId);
    return blob;
  } catch (error) {
    if (activityId) activityStore?.endActivity(activityId);
    console.error("Error fetching file:", error);
    throw error;
  }
}

export function downloadLegacy(downloadUrl: string, fileName: string): void {
  const apiUrl =
    process.env.REACT_APP_API_URL || "http://localhost:4000/graphql";
  const serverUrl = apiUrl.replace(/\/graphql$/, "");
  const link = document.createElement("a");
  link.href = `${serverUrl}/download${downloadUrl}`;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
